import React, { useEffect, useState } from "react";
import { useHistoryStep } from "../../lib/useHistoryStep";
import { api } from "../../lib/api";
import { Field, TextInput, TextArea, SelectInput, Consent } from "./formPrimitives";
import ImageUpload from "./ImageUpload";
import ModelPicker, { OTHER_ID } from "./ModelPicker";
import PartsQualityInfo, { SCREEN_PROBLEM, BATTERY_PROBLEM } from "./PartsQualityInfo";
import { CheckCircle2, ArrowRight, ArrowLeft, Loader2, CalendarClock, ShieldAlert, Calculator, Pencil } from "lucide-react";
import { formatNok } from "../../lib/api";
import { toast } from "sonner";

const fmtSlot = (d, t) => {
  if (!d || !t) return "–";
  const [y, m, day] = d.split("-");
  return `${day}.${m}.${y} kl. ${t}`;
};
const empty = {
  device_type: "", brand: "", model: "", problem: "", description: "", price_estimate: "",
  preferred_date: "", preferred_time: "", name: "", phone: "", email: "",
  image_id: null, consent: false, company: "",
  model_pick: null, model_other: "",
};

export default function BookingForm() {
  const [step, setStep] = useState(0);
  useHistoryStep("bookingStep", step, setStep);
  const [categories, setCategories] = useState([]);
  const [problems, setProblems] = useState([]);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [today, setToday] = useState(() => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Oslo" }).format(new Date()));
  const [refresh, setRefresh] = useState(0);
  const [models, setModels] = useState([]);
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    const onPrefill = (e) => {
      const { category, model, problems: picked, lines, total, estimate } = e.detail;
      const isOther = model?.id === OTHER_ID;
      const extra = picked.slice(1);
      setForm((f) => ({
        ...f,
        device_type: category,
        model_pick: model,
        model: model?.name || "",
        model_other: isOther ? model.name : "",
        brand: model?.brand || f.brand,
        problem: picked[0] || "",
        description: extra.length ? `Ønsker også: ${extra.join(", ")}` : f.description,
        price_estimate: estimate,
      }));
      setPrefill({ model: model?.name, lines, total });
      setErrors({});
      setDone(false);
      setStep(category && model && picked[0] && picked[0] !== "Annet" ? 1 : 0);
    };
    window.addEventListener("mobilhub:prefill-booking", onPrefill);
    return () => window.removeEventListener("mobilhub:prefill-booking", onPrefill);
  }, []);

  useEffect(() => {
    const cat = categories.find((c) => c.name === form.device_type);
    if (!cat) { setModels([]); return; }
    api.get(`/content/models?category_id=${cat.id}`).then((r) => setModels(r.data)).catch(() => setModels([]));
  }, [form.device_type, categories]);

  useEffect(() => {
    api.get("/content/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/content/problems").then((r) => setProblems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.preferred_date) { setSlots([]); return; }
    let active = true;
    setSlotsLoading(true);
    api.get("/bookings/availability", { params: { date: form.preferred_date } })
      .then((r) => {
        if (!active) return;
        setSlots(r.data.slots);
        setToday(r.data.today);
        const chosen = r.data.slots.find((s) => s.time === form.preferred_time);
        if (chosen && !chosen.available) setForm((f) => ({ ...f, preferred_time: "" }));
      })
      .catch(() => { if (active) setSlots([]); })
      .finally(() => { if (active) setSlotsLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.preferred_date, refresh]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.device_type) e.device_type = "Velg type enhet";
      if (!form.model_pick) e.model = "Velg modell";
      else if (form.model_pick.id === OTHER_ID && !form.model_other.trim()) e.model_other = "Skriv inn modellen din";
      if (!form.problem) e.problem = "Velg problem eller tjeneste";
      if (form.problem === "Annet" && !form.description.trim()) e.description = "Beskriv problemet";
    }
    if (step === 1) {
      if (!form.preferred_date) e.preferred_date = "Velg dato";
      if (!form.preferred_time) e.preferred_time = "Velg tidspunkt";
      if (!form.name.trim()) e.name = "Fyll inn navn";
      if (!/^[+\d][\d\s]{6,}$/.test(form.phone.trim())) e.phone = "Ugyldig telefonnummer";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "Ugyldig e-postadresse";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => s + 1); };
  const back = () => setStep((s) => s - 1);

  const submit = async () => {
    if (!form.consent) { toast.error("Du må godta personvernvilkårene"); return; }
    setSubmitting(true);
    try {
      const { model_pick, model_other, ...payload } = form;
      await api.post("/bookings", payload);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Noe gikk galt. Prøv igjen.");
      if (err.response?.status === 409 || err.response?.status === 400) {
        setForm((f) => ({ ...f, preferred_time: "" }));
        setRefresh((n) => n + 1);
        setStep(1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setForm(empty); setStep(0); setDone(false); setErrors({}); setPrefill(null); };

  if (done) {
    return (
      <section id="bestill" className="py-16 sm:py-24 bg-offwhite" data-testid="booking-section">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <div className="rounded-2xl border border-hairline bg-white p-8 text-center shadow-sm" data-testid="booking-confirmation">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600">
              <CheckCircle2 className="h-9 w-9" />
            </span>
            <h3 className="mt-5 font-heading text-2xl font-bold text-navy">Takk!</h3>
            <p className="mt-3 text-ink/70">
              Vi har mottatt forespørselen din. Du får en bekreftelse fra MobilHub så snart
              tidspunktet er godkjent.
            </p>
            <p className="mt-2 text-sm text-ink/50">
              Merk: Bestillingen er ikke endelig før MobilHub har bekreftet den.
            </p>
            <button
              onClick={reset}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-800"
              data-testid="booking-new-button"
            >
              Ny bestilling
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="bestill" className="py-16 sm:py-24 bg-offwhite" data-testid="booking-section">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <div className="max-w-xl">
          <span className="text-sm font-semibold uppercase tracking-widest text-electric">Bestill time</span>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-navy">Bestill reparasjon</h2>
          <p className="mt-3 text-ink/70">
            Fyll ut skjemaet, så tar vi kontakt for å bekrefte tidspunktet. Det tar ett minutt.
          </p>
        </div>

        <div className="mt-8 rounded-2xl border border-hairline bg-white p-5 sm:p-7 shadow-sm">
          <div className="mb-6 flex items-center gap-2">
            {["Enhet", "Kontakt", "Oppsummering"].map((s, i) => (
              <div key={s} className={`flex-1 rounded-full h-1.5 ${i <= step ? "bg-electric" : "bg-slate-200"}`} title={s} />
            ))}
          </div>

          {/* honeypot */}
          <input
            type="text" tabIndex={-1} autoComplete="off" value={form.company}
            onChange={(e) => set("company", e.target.value)}
            className="hidden" aria-hidden="true" data-testid="booking-honeypot"
          />

          {prefill && step < 2 && (
            <div className="mb-5 rounded-xl border border-electric/20 bg-electric/5 p-4 text-sm" data-testid="booking-prefill-summary">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-semibold text-navy"><Calculator className="h-4 w-4 text-electric" /> Fra priskalkulatoren</p>
                  <p className="mt-1 font-medium text-navy" data-testid="booking-prefill-model">{prefill.model}</p>
                  <ul className="mt-1 space-y-0.5 text-ink/75">
                    {prefill.lines.map((l) => (
                      <li key={l.problem} data-testid={`booking-prefill-line-${l.problem}`}>{l.problem} — {l.price === null ? "Pris etter diagnostikk" : formatNok(l.price)}</li>
                    ))}
                  </ul>
                  {prefill.lines.length > 1 && <p className="mt-1 font-semibold text-navy">Totalt: {prefill.total === null ? "Pris etter diagnostikk" : formatNok(prefill.total)}</p>}
                </div>
                {step === 1 && (
                  <button type="button" onClick={() => setStep(0)} className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-electric hover:underline" data-testid="booking-prefill-edit">
                    <Pencil className="h-3.5 w-3.5" /> Endre
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 0 && (
            <div className="space-y-4" data-testid="booking-step-1">
              <Field label="Type enhet" required error={errors.device_type}>
                <SelectInput value={form.device_type} onChange={(e) => { set("device_type", e.target.value); setForm((f) => ({ ...f, model_pick: null, model: "", model_other: "", price_estimate: "" })); setPrefill(null); }} error={errors.device_type} data-testid="booking-device-type">
                  <option value="">Velg ...</option>
                  {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </SelectInput>
              </Field>
              <Field label="Modell" required error={errors.model} as="div" hint={!form.device_type ? "Velg type enhet først" : undefined}>
                <ModelPicker
                  models={models}
                  categoryName={form.device_type}
                  value={form.model_pick}
                  onChange={(m) => { setForm((f) => ({ ...f, model_pick: m, model: m.id === OTHER_ID ? f.model_other : m.name, brand: m.brand || f.brand, price_estimate: m.id === f.model_pick?.id ? f.price_estimate : "" })); if (m.id !== form.model_pick?.id) setPrefill(null); setErrors((e) => ({ ...e, model: undefined })); }}
                  otherText={form.model_other}
                  onOtherText={(t) => { setForm((f) => ({ ...f, model_other: t, model: t })); setErrors((e) => ({ ...e, model_other: undefined })); }}
                  otherError={errors.model_other}
                  error={errors.model}
                  testid="booking-model"
                />
              </Field>
              <Field label="Merke" hint="F.eks. Apple, Samsung">
                <TextInput value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Merke" data-testid="booking-brand" />
              </Field>
              <Field label="Problem eller ønsket tjeneste" required error={errors.problem}>
                <SelectInput value={form.problem} onChange={(e) => { set("problem", e.target.value); if (e.target.value !== form.problem) { setPrefill(null); setForm((f) => ({ ...f, price_estimate: "" })); } }} error={errors.problem} data-testid="booking-problem">
                  <option value="">Velg ...</option>
                  {problems.map((p) => <option key={p} value={p}>{p}</option>)}
                  {form.problem && !problems.includes(form.problem) && <option value={form.problem}>{form.problem}</option>}
                </SelectInput>
              </Field>
              <Field label={form.problem === "Annet" ? "Beskriv problemet" : "Beskrivelse"} required={form.problem === "Annet"} hint={form.problem === "Annet" ? undefined : "Beskriv gjerne feilen nærmere"} error={errors.description}>
                <TextArea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={form.problem === "Annet" ? "Fortell kort hva som er galt med enheten." : "Tilleggsbeskrivelse"} error={errors.description} data-testid="booking-description" />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="booking-step-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Ønsket dato" required error={errors.preferred_date}>
                  <TextInput type="date" min={today || undefined} value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} error={errors.preferred_date} data-testid="booking-date" />
                </Field>
                <Field label="Ønsket tidspunkt" required error={errors.preferred_time} hint={!form.preferred_date ? "Velg dato først" : undefined}>
                  <SelectInput value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} disabled={!form.preferred_date || slotsLoading} error={errors.preferred_time} data-testid="booking-time">
                    <option value="">{slotsLoading ? "Laster ..." : slots.length && !slots.some((s) => s.available) ? "Ingen ledige tidspunkt denne dagen" : "Velg tidspunkt"}</option>
                    {slots.filter((s) => !s.past).map((s) => (
                      <option key={s.time} value={s.time} disabled={!s.available} data-testid={`booking-time-${s.time}`}>
                        {s.time}{s.available ? "" : " – opptatt"}
                      </option>
                    ))}
                  </SelectInput>
                </Field>
              </div>
              <Field label="Navn" required error={errors.name}>
                <TextInput value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Fullt navn" error={errors.name} data-testid="booking-name" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Telefonnummer" required error={errors.phone}>
                  <TextInput type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+47 ..." error={errors.phone} data-testid="booking-phone" />
                </Field>
                <Field label="E-postadresse" required error={errors.email}>
                  <TextInput type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="deg@epost.no" error={errors.email} data-testid="booking-email" />
                </Field>
              </div>
              <Field label="Bilde av enheten">
                <ImageUpload imageId={form.image_id} onChange={(id) => set("image_id", id)} testid="booking-image" />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="booking-step-3">
              <div className="rounded-xl border border-hairline bg-offwhite p-4">
                <h4 className="font-heading text-base font-bold text-navy mb-3">Se over bestillingen</h4>
                <dl className="space-y-1.5 text-sm">
                  {[
                    ["Enhet", [form.device_type, form.brand, form.model].filter((v, i, a) => v && !a.slice(i + 1).some((o) => o && o.toLowerCase().includes(v.toLowerCase()))).join(" ")],
                    ["Problem", form.problem],
                    ...(form.price_estimate ? [["Estimert pris", form.price_estimate]] : []),
                    ["Beskrivelse", form.description || "–"],
                    ["Ønsket tid", fmtSlot(form.preferred_date, form.preferred_time)],
                    ["Navn", form.name], ["Telefon", form.phone], ["E-post", form.email],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4">
                      <dt className="text-ink/50">{k}</dt>
                      <dd className="font-medium text-navy text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              {form.device_type.toLowerCase().includes("iphone") && (form.problem === SCREEN_PROBLEM || form.problem === BATTERY_PROBLEM) && (
                <PartsQualityInfo showScreens={form.problem === SCREEN_PROBLEM} showBatteries={form.problem === BATTERY_PROBLEM} testid="booking-parts-quality" />
              )}
              <div className="flex items-start gap-2 rounded-lg bg-warm/15 border border-warm/40 p-3.5 text-sm text-navy">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warm-dark" />
                <span>Bestillingen er <strong>ikke endelig</strong> før MobilHub har bekreftet tidspunktet. Du hører fra oss så snart som mulig.</span>
              </div>
              <Consent checked={form.consent} onChange={(v) => set("consent", v)} testid="booking-consent" />
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            {step > 0 ? (
              <button onClick={back} className="inline-flex items-center gap-1.5 text-sm font-medium text-electric" data-testid="booking-back">
                <ArrowLeft className="h-4 w-4" /> Tilbake
              </button>
            ) : <span />}
            {step < 2 ? (
              <button onClick={next} className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-800" data-testid="booking-next">
                Neste <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 rounded-full bg-warm px-6 py-3 text-sm font-semibold text-navy hover:bg-warm-dark disabled:opacity-50" data-testid="booking-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                Send bestilling
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
